import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import { Settings as SettingsIcon, Save, RefreshCw } from 'lucide-react';
import ConfirmModal from './ConfirmModal';

const Settings = () => {
  const [settings, setSettings] = useState({});
  const [publicSettings, setPublicSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, title: '', message: '' });
  const [leadViewsJson, setLeadViewsJson] = useState('');
  const [rowIntelligenceJson, setRowIntelligenceJson] = useState('');
  const [leadStatusesJson, setLeadStatusesJson] = useState('');
  const [leadSourcesJson, setLeadSourcesJson] = useState('');
  const [leadIntentsJson, setLeadIntentsJson] = useState('');
  const [leadPropertyTypesJson, setLeadPropertyTypesJson] = useState('');

  const showAlert = (message) => {
    setModalConfig({
      isOpen: true,
      title: 'Notification',
      message,
      confirmLabel: 'OK',
      confirmVariant: 'primary',
      onConfirm: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
      onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
    });
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [settingsRes, publicRes] = await Promise.all([
        settingsAPI.getAll(),
        settingsAPI.getPublic()
      ]);
      setSettings(settingsRes.data);
      setPublicSettings(publicRes.data.features || {});
      setLeadViewsJson(JSON.stringify(settingsRes.data?.lead_views_config || [], null, 2));
      setRowIntelligenceJson(JSON.stringify(settingsRes.data?.lead_row_intelligence_config || {}, null, 2));
      setLeadStatusesJson(JSON.stringify(settingsRes.data?.lead_statuses || ["new", "contacted", "in_progress", "follow_up", "negotiating", "won", "lost", "queued_unassigned", "archived"], null, 2));
      setLeadSourcesJson(JSON.stringify(settingsRes.data?.lead_sources || ["General", "Meta Ads", "Eighteen", "Referral", "Whatsapp referral", "Website", "Walk-in", "Phone Call"], null, 2));
      setLeadIntentsJson(JSON.stringify(settingsRes.data?.lead_intents || ["Buy", "Sell", "Rent"], null, 2));
      setLeadPropertyTypesJson(JSON.stringify(settingsRes.data?.lead_property_types || ["Apartment", "House", "Plot", "Commercial", "Studio", "Penthouse", "Other"], null, 2));
    } catch (error) {
      console.error('Failed to load settings:', error);
      showAlert('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings({
      ...settings,
      [key]: !settings[key]
    });
  };

  const handleChange = (key, value) => {
    setSettings({
      ...settings,
      [key]: value
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      let parsedLeadViews = settings.lead_views_config || [];
      let parsedRowRules = settings.lead_row_intelligence_config || {};
      let parsedLeadStatuses = settings.lead_statuses || [];
      let parsedLeadSources = settings.lead_sources || [];
      let parsedLeadIntents = settings.lead_intents || [];
      let parsedLeadPropertyTypes = settings.lead_property_types || [];
      
      try {
        parsedLeadViews = JSON.parse(leadViewsJson || '[]');
        parsedRowRules = JSON.parse(rowIntelligenceJson || '{}');
        parsedLeadStatuses = JSON.parse(leadStatusesJson || '[]');
        parsedLeadSources = JSON.parse(leadSourcesJson || '[]');
        parsedLeadIntents = JSON.parse(leadIntentsJson || '[]');
        parsedLeadPropertyTypes = JSON.parse(leadPropertyTypesJson || '[]');
      } catch (e) {
        showAlert('One or more JSON fields are invalid. Please fix and save again.');
        setSaving(false);
        return;
      }
      await settingsAPI.bulkUpdate({
        ...settings,
        lead_views_config: parsedLeadViews,
        lead_row_intelligence_config: parsedRowRules,
        lead_statuses: parsedLeadStatuses,
        lead_sources: parsedLeadSources,
        lead_intents: parsedLeadIntents,
        lead_property_types: parsedLeadPropertyTypes
      });
      showAlert('Settings saved successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      showAlert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary">System Settings</h1>
          <p className="text-text-secondary mt-1">Configure system features and preferences</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadSettings}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw size={20} />
            Refresh
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Feature Toggles */}
        <div className="card p-6">
          <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Feature Toggles</h2>
          <p className="text-sm text-text-secondary mb-6">Enable or disable features for sales agents</p>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Auto Lead Assignment</h3>
                <p className="text-sm text-gray-600">Automatically assign new leads to eligible agents</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.auto_assignment_enabled !== false}
                  onChange={() => handleToggle('auto_assignment_enabled')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Leads Management</h3>
                <p className="text-sm text-gray-600">Enable leads feature for sales agents</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.features_leads_enabled !== false}
                  onChange={() => handleToggle('features_leads_enabled')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Properties Management</h3>
                <p className="text-sm text-gray-600">Enable properties feature for sales agents</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.features_properties_enabled !== false}
                  onChange={() => handleToggle('features_properties_enabled')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Tasks Management</h3>
                <p className="text-sm text-gray-600">Enable tasks feature for sales agents</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.features_tasks_enabled !== false}
                  onChange={() => handleToggle('features_tasks_enabled')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Analytics & Reports</h3>
                <p className="text-sm text-gray-600">Enable analytics feature for sales agents</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.features_analytics_enabled !== false}
                  onChange={() => handleToggle('features_analytics_enabled')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Documents Management</h3>
                <p className="text-sm text-gray-600">Enable documents feature for sales agents</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.features_documents_enabled !== false}
                  onChange={() => handleToggle('features_documents_enabled')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Team Notes</h3>
                <p className="text-sm text-gray-600">Enable team notes feature for sales agents</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.features_team_notes_enabled !== false}
                  onChange={() => handleToggle('features_team_notes_enabled')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">System Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">System Name</label>
              <input
                type="text"
                value={settings.system_name || 'LuxeGrid CRM'}
                onChange={(e) => handleChange('system_name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                value={settings.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="Enter company name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max File Upload Size (MB)</label>
              <input
                type="number"
                value={settings.max_file_upload_size_mb || 10}
                onChange={(e) => handleChange('max_file_upload_size_mb', e.target.value)}
                min="1"
                max="100"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Leads Workspace (CMS)</h2>
          <p className="text-sm text-gray-600 mb-4">Manage lead views, column presets, and row-intelligence display logic from frontend.</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lead Views Config (JSON)</label>
              <textarea
                value={leadViewsJson}
                onChange={(e) => setLeadViewsJson(e.target.value)}
                rows={10}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Row Intelligence Config (JSON)</label>
              <textarea
                value={rowIntelligenceJson}
                onChange={(e) => setRowIntelligenceJson(e.target.value)}
                rows={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Statuses (JSON array)</label>
                <textarea
                  value={leadStatusesJson}
                  onChange={(e) => setLeadStatusesJson(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Sources (JSON array)</label>
                <textarea
                  value={leadSourcesJson}
                  onChange={(e) => setLeadSourcesJson(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Intents (JSON array)</label>
                <textarea
                  value={leadIntentsJson}
                  onChange={(e) => setLeadIntentsJson(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Property Types (JSON array)</label>
                <textarea
                  value={leadPropertyTypesJson}
                  onChange={(e) => setLeadPropertyTypesJson(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal {...modalConfig} />
    </div>
  );
};

export default Settings;

